/**
 * NOTE (jsatk): I enabled TypeScript in this file on July 11, 2019.  Putting in
 * *really good* types was a big challenge.  In the interest of time I opted to
 * do what I could and move on.  There are a lot of `*` types sprinkled about in
 * here.  If you are reading this I'd be wonderful if you could improve a type
 * or two in here before you leave.  Thank you.
 */

/**
 * The contents of the User dropdown in the Bibliotron global nav.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@repo/analytics-learning-time";
import {RequestInfo} from "@repo/shared-core";
import {getLogoutUrl, redirect} from "@repo/urls";

import {CLASS_CONSTANTS} from "#class-constants-package/constants.ts";
import LocalStore from "#shared-package/local-store.ts";

type OwnProps = {
    // The current URL. Used to generate the logout link.
    currentUrl?: string;
    // Whether or not the user has children.
    hasChildren?: boolean;
    // Whether or not the user has students.
    hasStudents?: boolean;
    // The URL of the user's profile.
    profileUrl: string;
    // Whether or not we should show the administrator links.
    showAdministratorLinks?: boolean;
    // Whether or not we should show the teaching links.
    showTeacherLinks?: boolean;
    // Whether or not we should show the parent links.
    showParentLinks?: boolean;
    // Whether a link to the settings page should be shown.
    showSettings?: boolean;
    // Whether the "sign up to save" link should be shown (for phantom
    // users who have gained points).
    showSignUpToSave?: boolean;
    // The URL to sign up.
    signUpUrl?: string;
    // The styles applied to the menu (root element of the component).
    style?: CSSProperties;
    // If the user is logged out and has taken a learning action.
    isPhantom: boolean;
    // The styles applied to each item in the menu.
    itemStyle?: CSSProperties;
    // Used to set the first link as a ref to a parent element for the purpose of focusing.
    captureFirstNavigableChildRef?: (arg1?: any) => void;
    // Clicking link should close the user dropdown under AppShell.
    onCloseDropdown?: () => void;
    // Used to navigate up the dropdown menu beyond this list.
    navigateToNotificationsSwitch: (arg1: React.KeyboardEvent) => void;
    // If we want users to be able to traverse this list using tab keys
    // (as opposed to arrow keys).
    tabbable?: boolean;
};

type Props = OwnProps & WithMarkConversionProps;

type State = {
    logoutUrl: string;
};

class UserSettings extends React.Component<Props, State> {
    state: State = {
        logoutUrl: "",
    };

    componentDidMount() {
        // TODO: We shouldn't set state immediately after mount.
        // Does `logoutUrl` really need to be held in component state?

        this.setState({
            logoutUrl: getLogoutUrl(this.props.currentUrl),
        });
    }

    // The list of links in this dropdown that the user can navigate to using arrow keys.
    navigableChildren: Array<null | HTMLAnchorElement | HTMLLIElement> = [];

    setLinkRef: (
        node: HTMLLIElement | HTMLAnchorElement | null,
        navigableListIndex: number,
    ) => void = (
        node: HTMLLIElement | HTMLAnchorElement | null,
        navigableListIndex: number,
    ) => {
        this.navigableChildren[navigableListIndex] = node;

        if (
            navigableListIndex === 0 &&
            this.props.captureFirstNavigableChildRef
        ) {
            this.props.captureFirstNavigableChildRef(node);
        }
    };

    getNavigableIndex: (elementName: string) => number = (
        elementName: string,
    ) => {
        const {
            showSignUpToSave,
            showAdministratorLinks,
            showTeacherLinks,
            showParentLinks,
            showSettings,
        } = this.props;

        let index = 0;

        // The contents of this list is highly variable depending on different variables,
        // so we just have to check each case to determine what the index of this element
        // is in the list.
        if (elementName === "signIn") {
            return index;
        }
        if (showSignUpToSave) {
            index++;
        }

        if (elementName === "learningHome") {
            return index;
        }
        index++;

        if (elementName === "coachDashboard") {
            return index;
        }
        if (showTeacherLinks) {
            index++;
        }

        if (elementName === "administratorDashboard") {
            return index;
        }
        if (showAdministratorLinks) {
            index++;
        }

        if (elementName === "parentDashboard") {
            return index;
        }
        if (showParentLinks) {
            index++;
        }

        if (elementName === "settings") {
            return index;
        }
        if (showSettings) {
            index++;
        }

        if (elementName === "help") {
            return index;
        }
        index++;

        return index;
    };

    getClickedNavigableIndex: (event: React.SyntheticEvent) => number = (
        event: React.SyntheticEvent,
    ): number => {
        // @ts-expect-error - TS2339 - Property 'parentNode' does not exist on type 'EventTarget'.
        const navigableIndexAttribute = event.target.parentNode.getAttribute(
            "data-navigable-index",
        );

        // If we do not have a navigableIndexAttribute then the index is 0 AKA
        // the first element in the list.
        return typeof navigableIndexAttribute === "undefined"
            ? 0
            : parseInt(navigableIndexAttribute);
    };

    handleLogoutClick: (event: React.SyntheticEvent) => void = (
        event: React.SyntheticEvent,
    ): void => {
        event.preventDefault();

        // For teachers, we store some classroom-related info in localStorage,
        // and this shouldn't persist past logout.
        CLASS_CONSTANTS.forEach((key) => {
            LocalStore.del(key);
        });

        const logoutUrl = getLogoutUrl(this.props.currentUrl);
        redirect(logoutUrl);
    };

    logSignInClicked: () => void = (): void => {
        this.props.markConversion({id: "global_nav_phantom_signin_clicked"});
    };

    handleArrowDownLink: (
        event: React.MouseEvent | React.KeyboardEvent,
    ) => void = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.preventDefault();

        const currentIndex = this.getClickedNavigableIndex(event);
        // If we're at the end of the list of links, we do nothing because there's
        // nowhere left to go.
        if (currentIndex === this.navigableChildren.length - 1) {
            return;
        }

        const nextElement = this.navigableChildren[currentIndex + 1];
        // @ts-expect-error - TS2531 - Object is possibly 'null'. | TS2531 - Object is possibly 'null'. | TS2339 - Property 'focus' does not exist on type 'ChildNode'.
        nextElement.firstChild.focus();
    };

    handleArrowUpLink: (event: React.KeyboardEvent) => void = (
        event: React.KeyboardEvent,
    ) => {
        event.preventDefault();

        const currentIndex = this.getClickedNavigableIndex(event);
        if (currentIndex === 0) {
            // We're at the first link, but because there's a separate button above
            // this list in the dropdown, we need to navigate to that link.
            this.props.navigateToNotificationsSwitch(event);
        } else {
            const nextElement = this.navigableChildren[currentIndex - 1];
            // @ts-expect-error - TS2531 - Object is possibly 'null'. | TS2531 - Object is possibly 'null'. | TS2339 - Property 'focus' does not exist on type 'ChildNode'.
            nextElement.firstChild.focus();
        }
    };

    handleLinkClick: (
        event: React.MouseEvent | React.KeyboardEvent,
        href: string,
    ) => void = (
        event: React.MouseEvent | React.KeyboardEvent,
        href: string,
    ) => {
        const {onCloseDropdown} = this.props;

        // We preventDefault and manually navigate to the link's destination in
        // because the normal link functionality interferes with us closing
        // the user dropdown.
        event.preventDefault();

        redirect(href);
        if (onCloseDropdown) {
            onCloseDropdown();
        }
    };

    // TODO(diedra): Consider writing a shared utility that can handle this up-down
    // navigation as this is used in every dropdown menu.
    handleKeyDownLink: (event: React.KeyboardEvent, href: string) => void = (
        event: React.KeyboardEvent,
        href: string,
    ) => {
        const {onCloseDropdown} = this.props;

        if (event.key === "Enter" || event.key === " ") {
            this.handleLinkClick(event, href);
        } else if (event.key === "Escape") {
            event.preventDefault();

            if (onCloseDropdown) {
                onCloseDropdown();
            }
        } else if (event.key === "Tab") {
            // We explicitly don't call event.preventDefault on tabbing so the user can
            // tab to the next node in the DOM tabbing order. We just close the menu
            // since they're leaving
            if (onCloseDropdown) {
                onCloseDropdown();
            }
        } else if (event.key === "ArrowDown") {
            this.handleArrowDownLink(event);
        } else if (event.key === "ArrowUp") {
            this.handleArrowUpLink(event);
        }
    };

    handleKeyDownLogSignIn: (event: React.KeyboardEvent) => void = (
        event: React.KeyboardEvent,
    ) => {
        const {profileUrl} = this.props;

        if (event.key === "Enter" || event.key === " ") {
            this.logSignInClicked();
        }

        this.handleKeyDownLink(event, profileUrl);
    };

    render(): React.ReactNode {
        const {
            showSettings,
            showSignUpToSave,
            showTeacherLinks,
            showParentLinks,
            showAdministratorLinks,
            signUpUrl,
            style,
            isPhantom,
            itemStyle,
            tabbable,
        } = this.props;

        const helpUrl = "https://khanacademy.zendesk.com";
        const coachDashboardUrl = "/teacher/dashboard";
        const learnerDashboardUrl = "/?learn=1";

        // NOTE(diedra): This is a list to communicate to screen readers how
        // many items there are in this dropdown list. If you refactor this
        // code, please keep the list structure <3
        return (
            <ul data-testid="header-user-settings" className={css(style)}>
                {showSignUpToSave && (
                    <li
                        data-navigable-index={this.getNavigableIndex("signIn")}
                        ref={(node) =>
                            this.setLinkRef(
                                node,
                                this.getNavigableIndex("signIn"),
                            )
                        }
                    >
                        <a
                            href={signUpUrl}
                            className={css(
                                styles.item,
                                styles.itemFirst,
                                styles.signIn,
                                itemStyle,
                            )}
                            onClick={this.logSignInClicked}
                            onKeyDown={this.handleKeyDownLogSignIn}
                            // We set tabbing to -1 so users will be sent to the next element
                            // in the tabbing order outside of this menu on tab.
                            tabIndex={tabbable ? 0 : -1}
                        >
                            <LabelMedium>
                                {t`Sign in to claim your points`}
                            </LabelMedium>
                        </a>
                    </li>
                )}
                <li
                    data-navigable-index={this.getNavigableIndex(
                        "learningHome",
                    )}
                    ref={(node) =>
                        this.setLinkRef(
                            node,
                            this.getNavigableIndex("learningHome"),
                        )
                    }
                >
                    <Link
                        href="/?learn=1"
                        style={[
                            styles.item,
                            !showSignUpToSave && styles.itemFirst,
                            itemStyle,
                        ]}
                        onClick={this.props.onCloseDropdown}
                        onKeyDown={(event) =>
                            this.handleKeyDownLink(event, learnerDashboardUrl)
                        }
                        // We set tabbing to -1 so users will be sent to the next element
                        // in the tabbing order outside of this menu on tab.
                        tabIndex={tabbable ? 0 : -1}
                        testId="learner-home-button"
                    >
                        <LabelMedium>{t`Learner home`}</LabelMedium>
                    </Link>
                </li>
                {showTeacherLinks && !RequestInfo.isRTL && (
                    <li
                        data-navigable-index={this.getNavigableIndex(
                            "coachDashboard",
                        )}
                        ref={(node) =>
                            this.setLinkRef(
                                node,
                                this.getNavigableIndex("coachDashboard"),
                            )
                        }
                    >
                        <Link
                            href="/teacher/dashboard"
                            style={[styles.item, itemStyle]}
                            onClick={this.props.onCloseDropdown}
                            onKeyDown={(event) =>
                                this.handleKeyDownLink(event, coachDashboardUrl)
                            }
                            // We set tabbing to -1 so users will be sent to the next element
                            // in the tabbing order outside of this menu on tab.
                            tabIndex={tabbable ? 0 : -1}
                        >
                            <LabelMedium>{t`Teacher dashboard`}</LabelMedium>
                        </Link>
                    </li>
                )}
                {showParentLinks && !RequestInfo.isRTL && (
                    <li
                        data-navigable-index={this.getNavigableIndex(
                            "parentDashboard",
                        )}
                        ref={(node) =>
                            this.setLinkRef(
                                node,
                                this.getNavigableIndex("parentDashboard"),
                            )
                        }
                    >
                        <Link
                            href="/parent"
                            className={css(styles.item, itemStyle)}
                            // We set tabbing to -1 so users will be sent to the next element
                            // in the tabbing order outside of this menu on tab.
                            tabIndex={tabbable ? 0 : -1}
                            onKeyDown={(event) =>
                                this.handleKeyDownLink(event, "/parent")
                            }
                        >
                            <LabelMedium>{t`Parent dashboard`}</LabelMedium>
                        </Link>
                    </li>
                )}
                {showAdministratorLinks && !RequestInfo.isRTL && (
                    <li
                        data-navigable-index={this.getNavigableIndex(
                            "administratorDashboard",
                        )}
                        ref={(node) =>
                            this.setLinkRef(
                                node,
                                this.getNavigableIndex(
                                    "administratorDashboard",
                                ),
                            )
                        }
                    >
                        <Link
                            href="/districts"
                            className={css(styles.item, itemStyle)}
                            // We set tabbing to -1 so users will be sent to the next element
                            // in the tabbing order outside of this menu on tab.
                            tabIndex={tabbable ? 0 : -1}
                            onKeyDown={(event) =>
                                this.handleKeyDownLink(event, "/districts")
                            }
                        >
                            <LabelMedium>
                                {t`Administrator dashboard`}
                            </LabelMedium>
                        </Link>
                    </li>
                )}
                {showSettings && (
                    <li
                        data-navigable-index={this.getNavigableIndex(
                            "settings",
                        )}
                        ref={(node) =>
                            this.setLinkRef(
                                node,
                                this.getNavigableIndex("settings"),
                            )
                        }
                    >
                        <Link
                            href="/settings/account"
                            className={css(styles.item, itemStyle)}
                            // We set tabbing to -1 so users will be sent to the next element
                            // in the tabbing order outside of this menu on tab.
                            tabIndex={tabbable ? 0 : -1}
                            onKeyDown={(event) =>
                                this.handleKeyDownLink(
                                    event,
                                    "/settings/account",
                                )
                            }
                        >
                            <LabelMedium>{t`Settings`}</LabelMedium>
                        </Link>
                    </li>
                )}
                <li
                    data-navigable-index={this.getNavigableIndex("help")}
                    ref={(node) =>
                        this.setLinkRef(node, this.getNavigableIndex("help"))
                    }
                >
                    <Link
                        href={helpUrl}
                        className={css(
                            styles.item,
                            isPhantom && styles.itemLast,
                            itemStyle,
                        )}
                        // We set tabbing to -1 so users will be sent to the next element
                        // in the tabbing order outside of this menu on tab.
                        tabIndex={tabbable ? 0 : -1}
                        onKeyDown={(event) =>
                            this.handleKeyDownLink(event, helpUrl)
                        }
                    >
                        <LabelMedium>{t`Help`}</LabelMedium>
                    </Link>
                </li>
                {!isPhantom && (
                    <li
                        data-navigable-index={this.getNavigableIndex("logout")}
                        ref={(node) =>
                            this.setLinkRef(
                                node,
                                this.getNavigableIndex("logout"),
                            )
                        }
                    >
                        <Link
                            className={css(
                                styles.item,
                                styles.itemLast,
                                itemStyle,
                            )}
                            onClick={this.handleLogoutClick}
                            onKeyDown={(event) =>
                                this.handleKeyDownLink(
                                    event,
                                    this.state.logoutUrl,
                                )
                            }
                            href={this.state.logoutUrl}
                            rel="nofollow"
                            // We set tabbing to -1 so users will be sent to the next element
                            // in the tabbing order outside of this menu on tab.
                            tabIndex={tabbable ? 0 : -1}
                            data-testid="user-settings-logout-link"
                        >
                            <LabelMedium>{t`Log out`}</LabelMedium>
                        </Link>
                    </li>
                )}
            </ul>
        );
    }
}

const styles = StyleSheet.create({
    item: {
        display: "block",
        padding: `${spacing.small_12}px ${spacing.medium_16}px`,
        backgroundColor: color.white,
        textDecoration: "none",

        ":hover": {
            textDecoration: "underline",
        },
    },

    itemFirst: {
        paddingTop: spacing.large_24,
    },

    itemLast: {
        paddingBottom: spacing.large_24,
        // This margin gives this button enough space to show the full focus outline.
        marginBottom: 1,
    },

    signIn: {
        color: color.green,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof UserSettings,
        React.ComponentProps<typeof UserSettings>
    >
>;

/**
 * TODO(somewhatabstract): This file is not properly annotated for learning
 * time, but we've decided it's not currently a priority.
 */
export default withMarkConversion(
    UserSettings,
) as React.ComponentType<ExportProps>;
